import { FormEvent, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Alert,
  Button,
  Paper,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { isAuthError, useAuth } from '../../auth/AuthContext';
import { useLocale } from '../../i18n/LocaleContext';
import type { Locale, MessageKey } from '../../i18n/translations';
import { useStyles } from './ProfileStyles';

const Profile = () => {
  const classes = useStyles();
  const { locale, setLocale, t } = useLocale();
  const { user, logout, changePassword } = useAuth();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<MessageKey | null>(null);
  const [success, setSuccess] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    setSuccess(false);

    if (newPassword !== confirmPassword) {
      setError('passwordMismatch');
      return;
    }

    setSubmitting(true);
    try {
      await changePassword(currentPassword, newPassword);
      setSuccess(true);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (cause) {
      setError(isAuthError(cause) ? (cause.code as MessageKey) : 'authError');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={classes.page}>
      <div className={classes.header}>
        <Typography
          variant="h4"
          sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}
        >
          {t('profile')}
        </Typography>
        <div className={classes.headerActions}>
          <Button component={Link} to="/schedule" variant="outlined">
            {t('backToSchedule')}
          </Button>
        </div>
      </div>

      <div className={classes.content}>
        <Paper className={classes.card} elevation={1}>
          <Typography variant="body1" color="text.secondary">
            {t('signedInAs', { username: user?.username ?? '' })}
          </Typography>

          <div className={classes.languageSection}>
            <Typography variant="h6">{t('language')}</Typography>
            <ToggleButtonGroup
              exclusive
              size="small"
              value={locale}
              onChange={(_, value: Locale | null) => {
                if (value) setLocale(value);
              }}
              aria-label={t('language')}
              className={classes.languageToggle}
            >
              <ToggleButton value="en">EN</ToggleButton>
              <ToggleButton value="ro">RO</ToggleButton>
            </ToggleButtonGroup>
          </div>

          <form className={classes.form} onSubmit={onSubmit}>
            <Typography variant="h6">{t('changePassword')}</Typography>
            {error && <Alert severity="error">{t(error)}</Alert>}
            {success && <Alert severity="success">{t('passwordChanged')}</Alert>}
            <TextField
              label={t('currentPassword')}
              type="password"
              value={currentPassword}
              onChange={(event) => setCurrentPassword(event.target.value)}
              autoComplete="current-password"
              required
              fullWidth
            />
            <TextField
              label={t('newPassword')}
              type="password"
              value={newPassword}
              onChange={(event) => setNewPassword(event.target.value)}
              autoComplete="new-password"
              required
              fullWidth
            />
            <TextField
              label={t('confirmPassword')}
              type="password"
              value={confirmPassword}
              onChange={(event) => setConfirmPassword(event.target.value)}
              autoComplete="new-password"
              required
              fullWidth
            />
            <div className={classes.actions}>
              <Button type="submit" variant="contained" disabled={submitting} fullWidth>
                {t('savePassword')}
              </Button>
              <Button
                type="button"
                variant="outlined"
                color="inherit"
                onClick={() => logout()}
                fullWidth
              >
                {t('logout')}
              </Button>
            </div>
          </form>
        </Paper>
      </div>
    </div>
  );
};

export default Profile;
